<template>
  <div>
    <Popover ref="filterPopover" :showHeader="true" :headerTitle="title">
      <div class="popover-filter-content">
        <div class="row">
          <div v-for="column of filterColumns" :key="column.index" :class="column.columnClass">
            <ul class="popover-filter-list" v-for="(filterType, filterIndex) in columnFilterList(column.index)" :key="filterIndex">
              <span class="font-weight-bold">{{filterType.name}}</span>
              <li v-for="(filter, index) in filterType.options" :key="`item-${filterIndex}-${index}`">
                <span :class="['popover-filter-slot', filter.class]" :style="stylesForFilter(filter)" @click.prevent="toggleFilter(filter)">
                  <i :class="['fas', 'fa-fw', filter.icon]" v-if="filter.icon"></i> 
                  {{(filter.name+'').trim() != '' ? filter.name : '&nbsp;'}}
                  <span class="popover-filter-slot-tick" v-if="isFilterSelected(filter)"><i class="fas fa-check"></i></span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="popover-filter-options">
        <FormButton class="btn btn-xs btn-warning" @click.prevent="clearFilters"><i class="far fa-times"></i> Clear Filter</FormButton>
      </div>
    </Popover>

    <div class="input-group">
      <div class="input-group-prepend filter-input-button" @click="displayFilterPopover">
        <span class="input-group-addon"><i :class="[hasSelectedFilters ? 'text-info' : '', hasSelectedFilters ? 'fas' : 'far', 'fa-filter']"></i></span>
      </div>
      <div class="form-control" v-if="hasSelectedFilters && showSelected">
        <span v-for="(filter, index) in selectedFilters" :key="`${index}-${filter.name}`" :style="stylesForFilter(filter)" :class="['label', 'filter-input-tag', filter.class]" @click.prevent="toggleFilter(filter)">{{filter.name}}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
ul.popover-filter-list {
  padding: 0px;
  list-style-type: none;
}
.popover-filter-list > li {
  margin-bottom: 6px;
}
.popover-filter-content {
  padding: 12px 12px 0px 12px;
}
.popover-filter-slot {
  width: 210px;
  height: 32px;
  padding: 6px;
  text-align: left;
  border-radius: 10px;
  font-weight: 500;
  font-size: 13px;
  display: inline-block;
  cursor: pointer;
}
.popover-filter-slot:hover {
  box-shadow: 0px 3px 0px 0px rgba(0, 0, 0, 0.2);
}
.popover-filter-slot:active {
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.2);
  transform: translateY(1px);
}
.popover-filter-options {
  border-top: 1px solid #e7eaec;
  padding: 8px;
}
.popover-filter-slot-tick {
  float: right;
  margin-right: 8px;
}
.popover-filter-slot > .popover-filter-slot-tick > i {
  color: inherit;
}
.filter-input-button {
  cursor: pointer;
  height: 35px;
}
.filter-input-tag {
  cursor: pointer;
  margin-right: 3px;
}
</style>

<script>
import _ from 'lodash'
import ColourMixin from '@/mixins/colour'
import Popover from '@/components/popover.vue'

export default {
  name: 'FilterInput',
  mixins: [ColourMixin],
  model: {
    event: 'update'
  },
  components: {
    Popover
  },
  props: {
    title: {
      type: String,
      default: 'Filter'
    },
    value: Array,
    filterOptions: Array,
    showSelected: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    filterOptions() {
      this.$data.filterList = this.buildFilterList(this.filterOptions)
      this.$data.filterColumns = this.buildColumns(this.filterOptions)
    },
    value: function() {
      this.updateSelectedList()
    }
  },
  computed: {
    hasSelectedFilters() {
      return this.selectedFilters.length > 0
    }
  },
  mounted() {
  },
  data() {
    return {
      filterList: this.buildFilterList(this.filterOptions),
      selectedFilters: _.cloneDeep(this.value || []),
      filterColumns: this.buildColumns(this.filterOptions)
    }
  },
  methods: {
    buildFilterList(list) {
      var outputList = _.cloneDeep(list || [])

      outputList.forEach(filterOption => {
        filterOption.columnIndex = filterOption.columnIndex || 0
        filterOption.options.forEach(option => {
          option.field = filterOption.field
        })
      })

      return outputList
    },
    buildColumns(list) {
      if(list == null)
        return []
      var columnCount = 1
      list.forEach(filterOption => {
        let columnIndex = filterOption.columnIndex || 0
        columnCount = Math.max(columnIndex+1, columnCount)
      })

      if(columnCount == 0)
        return []

      let columnClass = `col-` + Math.floor(12 / columnCount)
      
      var colList = []
      for(var i=0; i<columnCount; i++) {
        colList.push({ index: i, columnClass: columnClass })
      }
      return colList
    },
    columnFilterList(index) {
      return this.filterList.filter(filter => filter.columnIndex == index)
    },
    isFilterSelected(filter) {
      if(!this.selectedFilters)
        return false
      return this.selectedFilters.find(selected => selected.field == filter.field && selected.value == filter.value) != null
    },
    toggleFilter(filter) {
      if(!this.isFilterSelected(filter))
        this.$data.selectedFilters.push(_.merge(filter, { field: filter.field }))
      else
        this.$data.selectedFilters = this.$data.selectedFilters.filter(selected => !(selected.field == filter.field && selected.value == filter.value))

      this.$emit('update', this.selectedFilters)
    },
    clearFilters() {
      this.$data.filterList.forEach(filterOption => {
        filterOption.options.forEach(option => {
          option.selected = false
        })
      })

      this.selectedFilters = []
      this.$emit('update', this.selectedFilters)
    },
    hideFilterPopover() {
      this.$refs.filterPopover.hidePopover()
    },
    displayFilterPopover(evt) {
      this.$refs.filterPopover.displayFromElement(evt.target)
    },
    stylesForFilter(filter) {
      if(!filter.colour)
        return {}
      return {
        'background-color': filter.colour,
        'color': this.textColourForBackground(filter.colour)
      }
    },
    updateSelectedList() {
      this.selectedFilters = []
      var selectedList = this.value || []

      // Build selected filters
      this.filterList.forEach(filterOption => {
        filterOption.options.forEach(option => {

          if(selectedList.find(selected => selected.field == option.field && selected.value == option.value) != null) {
            this.selectedFilters.push(_.merge(option, { field: filterOption.field }))
          }
        })
      })
    }
  }
}
</script>